import { FileResponse } from '@business/entities/file';
import { OrgId } from '@business/entities/organization';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { BehaviorSubject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { FolderBreadcrumbResponse, FolderId, FolderResponse, FileId } from './../../entities/file/FolderResponse';
import { TenantIdentity } from '@business/entities/common';
import { SharedFileAPI } from '@business/api/sharedFile';
import { FormId } from '@business/entities/form';

export class SharedFileBloc extends BaseBloc {
  private readonly itemsRepo = new Repository<FileResponse | FolderResponse>({
    getItemId: item => `${item.type}-${item.id}`,
  });

  readonly items$ = this.itemsRepo.items$.pipe(
    map(items => {
      return R.orderBy(items, ['type', 'name'], ['desc', 'asc']);
    }),
  );
  readonly breadcrumb$ = new BehaviorSubject<FolderBreadcrumbResponse[]>([]);

  onReset() {
    this.itemsRepo.reset();
  }

  getItems = (orgId: OrgId, parentId?: FolderId, isPublic?: boolean, identity?: TenantIdentity) => {
    return SharedFileAPI.getItems(orgId, parentId, isPublic, identity).pipe(
      map(data => {
        this.breadcrumb$.next([{ id: '0', name: 'Files' }, ...data.breadcrumbs]);
        return data.items;
      }),
      this.itemsRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_files_error') }),
    );
  };

  createFolder = (orgId: OrgId, name: string, parentId?: FolderId, isPublic?: boolean, identity?: TenantIdentity) => {
    return SharedFileAPI.createFolder(orgId, name, parentId, isPublic, identity).pipe(
      this.itemsRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_folder'),
      }),
    );
  };

  renameFolder = (id: FolderId, name: string, identity?: TenantIdentity) => {
    return SharedFileAPI.renameFolder(id, name, identity).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('rename_folder'),
      }),
    );
  };

  deleteFolder = (id: FolderId, identity?: TenantIdentity) => {
    return showDeleteConfirmation('Delete Folder', 'Do you really want to remove this folder?').pipe(
      switchMap(() => SharedFileAPI.deleteFolder(id, identity)),
      this.itemsRepo.ops.removeOne(() => `folder-${id}`),
      handleMessage({
        type: requestMessage('delete_folder'),
      }),
    );
  };

  search = (orgId: OrgId, search: string, parentid?: FolderId, identity?: TenantIdentity) => {
    return SharedFileAPI.search(orgId, search, parentid, identity).pipe(
      handleMessage({
        error: requestMessage('fetch_general_error'),
      }),
    );
  };

  createDocument = (orgId: OrgId, name: string, folderId: FolderId, identity?: TenantIdentity) => {
    return SharedFileAPI.createDocument(orgId, name, folderId, identity).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_document'),
      }),
    );
  };

  createForm = (orgId: OrgId, name: string, folderId: FolderId, form: FormId, identity?: TenantIdentity) => {
    return SharedFileAPI.createForm(orgId, name, folderId, form, identity).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_form_file'),
      }),
    );
  };

  saveFormValues = (id: string, data: string, identity?: TenantIdentity) => {
    return SharedFileAPI.saveFormValues(id, data, identity).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('save_form_values'),
      }),
    );
  };

  reloadFile = (id: string, identity?: TenantIdentity) => {
    return SharedFileAPI.reloadFile(id, identity).pipe(this.itemsRepo.ops.upsertOne(item => ({ item })));
  };

  renameFile = (id: FileId, name: string, identity?: TenantIdentity) => {
    return SharedFileAPI.renameFile(id, name, identity).pipe(
      this.itemsRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('rename_file'),
      }),
    );
  };

  deleteFile = (id: FileId, identity?: TenantIdentity) => {
    return showDeleteConfirmation('Delete File', 'Do you really want to remove this file?').pipe(
      switchMap(() => SharedFileAPI.deleteFile(id, identity)),
      this.itemsRepo.ops.removeOne(() => `file-${id}`),
      handleMessage({
        type: requestMessage('delete_file'),
      }),
    );
  };
}
